'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Download, File as FileIcon, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  isHistoricFileReference,
  type HistoricFileReference,
} from '@/lib/camunda/form-file-values';
import { CamundaHistoryService } from '@/lib/camunda/history-service';
import { useFormModeGuard } from '@/forms/shared/components/use-form-mode-guard';
import { PdfFilePreview } from '@/forms/shared/components/pdf-file-preview';
import { useObjectUrlLifecycle } from './multi-file-object-url';

interface FileFieldProps {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  accept?: string;
}

interface ResolvedFileDisplay {
  file: File | null;
  fileName: string;
  mimeType: string;
  hasContent: boolean;
  historicReference: HistoricFileReference | null;
}

function resolveFileDisplay(value: File | HistoricFileReference): ResolvedFileDisplay {
  if (value instanceof File) {
    return {
      file: value,
      fileName: value.name,
      mimeType: value.type,
      hasContent: value.size > 0,
      historicReference: null,
    };
  }

  const mimeType = value.mimeType ?? 'application/octet-stream';
  return {
    file: null,
    fileName: value.filename,
    mimeType,
    hasContent: true,
    historicReference: value,
  };
}

export function FileField({
  name,
  label,
  required = false,
  disabled = false,
  className,
  accept,
}: FileFieldProps) {
  const form = useFormContext();
  const { sideEffectsAllowed } = useFormModeGuard(disabled);
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const historicBlobRef = useRef<{
    historicDetailId: string;
    blob: Blob;
  } | null>(null);
  const [loadingHistoricFile, setLoadingHistoricFile] = useState(false);
  const [historicFileError, setHistoricFileError] = useState<string | null>(null);
  const { replaceObjectUrl, releaseObjectUrl } = useObjectUrlLifecycle();

  const fileObject = form.watch(name);
  const resolvedFile = fileObject instanceof File
    ? resolveFileDisplay(fileObject)
    : isHistoricFileReference(fileObject)
      ? resolveFileDisplay(fileObject)
      : null;
  const currentFileName = resolvedFile?.fileName ?? null;
  const isPdf = resolvedFile
    && (resolvedFile.mimeType === 'application/pdf'
      || resolvedFile.fileName.toLowerCase().endsWith('.pdf'));
  const canDownload = Boolean(resolvedFile?.hasContent);
  const canPreview = canDownload && isPdf;
  const fileIdentity = resolvedFile?.file ?? resolvedFile?.historicReference?.historicDetailId ?? null;

  useEffect(() => releaseObjectUrl, [fileIdentity, releaseObjectUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!sideEffectsAllowed) return;
    const selectedFile = event.target.files?.[0] ?? null;
    form.setValue(name, selectedFile, { shouldValidate: true });
  };

  const loadHistoricBlob = async (
    reference: HistoricFileReference,
  ): Promise<Blob> => {
    if (historicBlobRef.current?.historicDetailId === reference.historicDetailId) {
      return historicBlobRef.current.blob;
    }
    setLoadingHistoricFile(true);
    setHistoricFileError(null);
    try {
      const blob = await CamundaHistoryService.downloadParticipationHistoricFile(reference);
      historicBlobRef.current = {
        historicDetailId: reference.historicDetailId,
        blob,
      };
      setLoadingHistoricFile(false);
      return blob;
    } catch (error) {
      setHistoricFileError(
        error instanceof Error ? error.message : 'No se pudo cargar el archivo histórico',
      );
      setLoadingHistoricFile(false);
      throw error;
    }
  };

  const resolveDownloadFile = async (): Promise<File | null> => {
    if (!resolvedFile) return null;
    if (resolvedFile.file) return resolvedFile.file;
    if (!resolvedFile.historicReference) return null;
    const blob = await loadHistoricBlob(resolvedFile.historicReference);
    return new File([blob], resolvedFile.fileName, { type: resolvedFile.mimeType });
  };

  const handleDownload = async () => {
    try {
      const file = await resolveDownloadFile();
      if (!file) return;

      const url = replaceObjectUrl(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
    }
  };

  const handleClear = () => {
    if (!sideEffectsAllowed) return;
    form.setValue(name, null, { shouldValidate: true });
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={() => (
        <FormItem className={className}>
          <FormLabel>{label} {required && '*'}</FormLabel>
          <FormControl>
            <div className="flex items-center space-x-2">
              {currentFileName ? (
                <div className="flex flex-grow items-center justify-between rounded-md border bg-muted p-2">
                  <div className="flex min-w-0 items-center space-x-2">
                    <FileIcon className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate text-sm">{currentFileName}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {canPreview && (
                      <PdfFilePreview
                        fileName={currentFileName}
                        loadFile={resolveDownloadFile}
                        disabled={loadingHistoricFile}
                        onError={setHistoricFileError}
                      />
                    )}
                    {canDownload && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => void handleDownload()}
                        disabled={loadingHistoricFile}
                        title="Descargar"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    {sideEffectsAllowed && (
                      <Button type="button" variant="ghost" size="icon" onClick={handleClear} title="Eliminar">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ) : !sideEffectsAllowed ? (
                <div className="flex flex-grow items-center rounded-md border bg-muted p-2 text-sm text-muted-foreground">
                  Sin archivo
                </div>
              ) : (
                <Input
                  id={inputId}
                  ref={inputRef}
                  type="file"
                  accept={accept}
                  disabled={!sideEffectsAllowed}
                  onChange={handleFileChange}
                  className="cursor-pointer"
                />
              )}
            </div>
          </FormControl>
          {historicFileError ? (
            <p className="text-sm text-destructive">{historicFileError}</p>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
